import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { keys } from './hooks.js';

/**
 * Everything the realtime handlers in realtime.ts keep fresh, by key prefix. Events sent while the socket was
 * down are lost, so after a reconnect each of these is invalidated: the ones on screen refetch at once, the
 * rest when next shown. Keep this in step with `Realtime.handle` (realtime-resync.test.ts checks it).
 */
export const RESYNC_KEYS: readonly QueryKey[] = [
  // message.created / message.updated: the open channel, reply panels, item glances.
  ['messages'],
  ['chatConversation'],
  ['glance'],
  // sidebar.changed, channel.read, inbox.changed (badge), workspace.changed: channel list and unread counts.
  keys.sidebar,
  // inbox.changed: the inbox lists, reminders, my work.
  keys.inbox,
  ['reminders'],
  keys.myWork,
  // work_item.changed: lists, open items, projects, channel headers, my day.
  ['workItems'],
  ['workItem'],
  ['projects'],
  ['channel'],
  keys.myDay,
  // birthdays.changed
  keys.birthdays,
  // workspace.changed, profile.changed: settings (incl. the person's time zone), profiles, members.
  ['settings'],
  ['profile'],
  keys.members,
  // A new or removed profile photo (docs/adr/0075).
  keys.peopleAvatars,
  // status.changed, availability.changed, my_availability.changed, catch_up.presence.
  keys.peopleStatuses,
  // catch_up.changed
  keys.catchUps,
  // chapi.delta / chapi.done (an answer that finished meanwhile) and chapi.changed.
  ['chapi', 'conversation'],
  keys.chapiConversations,
  ['chapi', 'breakdown'],
  keys.chapiDoneSuggestions,
  keys.chapiPendingUpdates,
  // chapi.chat_reply and chapi.task_suggestions (docs/adr/0079): answers in conversations, spotted tasks, nudges.
  ['chapi', 'chat'],
];

/** Marks every realtime-kept query stale; active ones refetch. Online dots come back with `presence.snapshot`. */
export function resyncAfterReconnect(qc: QueryClient): void {
  for (const queryKey of RESYNC_KEYS) void qc.invalidateQueries({ queryKey });
}

/**
 * The persisted cache (queryClient.ts) is back from IndexedDB: it renders at once, but it may be from before events
 * that no socket of this page heard (a guest's comment card that arrived while the tab was closed or reloading), and
 * its timestamps can still look fresh to the 15-second stale time, so nothing would refetch it while the person stays
 * on the screen. Everything the events keep fresh is marked stale: what is on screen refetches now, the rest when shown.
 */
export function revalidateRestoredCache(qc: QueryClient): void {
  resyncAfterReconnect(qc);
}

export interface ResyncTiming {
  /** The connection must stay up this long before the refetch runs. */
  settleMs: number;
  /** At most one refetch per this long, however often the connection flaps. */
  minIntervalMs: number;
}

export const RESYNC_TIMING: ResyncTiming = { settleMs: 750, minIntervalMs: 10_000 };

/**
 * Debounces the refetch after reconnects so a flapping connection does not stampede the API: it runs once the
 * socket has stayed open for `settleMs`, never more often than every `minIntervalMs`, and a drop before it runs
 * cancels it (the next reconnect schedules it again, so nothing is skipped).
 */
export class ResyncScheduler {
  private timer: ReturnType<typeof setTimeout> | undefined;
  private lastRun = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly run: () => void,
    private readonly timing: ResyncTiming = RESYNC_TIMING,
  ) {}

  /** The socket reconnected. */
  schedule(): void {
    this.cancel();
    const delay = Math.max(this.timing.settleMs, this.lastRun + this.timing.minIntervalMs - Date.now());
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.lastRun = Date.now();
      this.run();
    }, delay);
  }

  /** The socket dropped (or stopped) before the refetch ran. */
  cancel(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  get pending(): boolean {
    return this.timer !== undefined;
  }
}
